import { useEffect, useMemo, useRef, useState } from 'react';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { SuperAdminPageHelp } from '@/components/SuperAdmin/SuperAdminPageHelp';
import { ProjectSearchSelect } from '@/components/ProjectSearchSelect';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { API_BASE_URL } from '@/config/apiBaseUrl';

type LogLevel = 'info' | 'success' | 'warning' | 'error' | 'done';

type StreamEvent = {
  ts?: string;
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
};

function levelClass(level: LogLevel): string {
  switch (level) {
    case 'success':
      return 'text-emerald-300';
    case 'warning':
      return 'text-amber-300';
    case 'error':
      return 'text-red-300';
    case 'done':
      return 'text-sky-300';
    default:
      return 'text-slate-200';
  }
}

export function SuperCheckDeviceExistPage() {
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();

  const [selectedProjectIri, setSelectedProjectIri] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isRunning, setIsRunning] = useState(false);
  const [lines, setLines] = useState<StreamEvent[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  const tailRef = useRef<HTMLDivElement | null>(null);

  const platformProjectId = useMemo(() => {
    if (!selectedProjectIri) return null;
    const n = parseInt(selectedProjectIri.replace('/api/projects/', ''), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedProjectIri]);

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    tailRef.current?.scrollIntoView({ block: 'end' });
  }, [lines.length]);

  const stop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsRunning(false);
  };

  const start = async () => {
    if (!platformProjectId) return;

    stop();
    setError('');
    setLines([]);
    setIsRunning(true);

    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Token manquant (non authentifié).');
      setIsRunning(false);
      return;
    }

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const url = new URL(`${API_BASE_URL}/admin/super-check-device-exist/stream`);
      url.searchParams.set('platformProjectId', String(platformProjectId));

      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        signal: ctrl.signal,
      });

      if (!res.ok) {
        const bodyText = await res.text();
        setError(bodyText || `HTTP ${res.status}`);
        setIsRunning(false);
        return;
      }

      if (!res.body) {
        setError('Réponse streaming invalide.');
        setIsRunning(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const pushLine = (evt: StreamEvent) => {
        setLines((prev) => [...prev, evt]);
        if (evt.level === 'done') setIsRunning(false);
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by a blank line (\n\n)
        while (true) {
          const sepIdx = buffer.indexOf('\n\n');
          if (sepIdx === -1) break;

          const rawEvent = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);

          const dataLines = rawEvent
            .split('\n')
            .map((l) => l.trimEnd())
            .filter((l) => l.startsWith('data:'));

          if (dataLines.length === 0) continue;

          const jsonText = dataLines.map((l) => l.slice('data:'.length).trimStart()).join('\n');
          try {
            const parsed = JSON.parse(jsonText) as StreamEvent;
            if (parsed && typeof parsed.level === 'string' && typeof parsed.message === 'string') {
              pushLine(parsed);
            }
          } catch {
            // ignore malformed chunk
          }
        }
      }

      setIsRunning(false);
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      setError(e?.message || 'Erreur');
      setIsRunning(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Super utilisateur</span> / Check devices Zipato
          </div>
          <div className="flex items-center gap-4">
            <SuperAdminPageHelp />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              <span className="material-icons-outlined">{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <span className="material-icons-outlined text-lg">person</span>
              <span>{user?.fullName || 'Admin'}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Vérifier devices (Zipato)</h1>

            <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Platform Project</label>
                <ProjectSearchSelect value={selectedProjectIri} onChange={setSelectedProjectIri} />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled={!platformProjectId || isRunning}
                  onClick={() => void start()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                >
                  <span className="material-icons-outlined text-lg">play_arrow</span>
                  Lancer
                </button>
                <button
                  type="button"
                  disabled={!isRunning}
                  onClick={stop}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#16202A] text-slate-800 dark:text-slate-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                >
                  <span className="material-icons-outlined text-lg">stop</span>
                  Stop
                </button>
                <button
                  type="button"
                  disabled={isRunning || lines.length === 0}
                  onClick={() => setLines([])}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#16202A] text-slate-800 dark:text-slate-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                >
                  <span className="material-icons-outlined text-lg">delete</span>
                  Effacer
                </button>

                {isRunning && <span className="text-sm text-slate-500 dark:text-slate-400">En cours...</span>}
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              <div className="rounded-lg border border-slate-200 dark:border-[#1E293B] bg-slate-50 dark:bg-[#0B1116] px-4 py-3 text-sm space-y-2">
                <div className="font-medium text-slate-900 dark:text-white">Résultats</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Sortie live (style terminal). Les lignes en rouge = device absent sur Zipato ou erreur; orange = warning; vert = résumé OK.
                </div>
                <div className="mt-2 p-3 rounded-md bg-[#0B1116] text-slate-100 overflow-x-auto text-xs leading-5 border border-slate-700 max-h-[520px] overflow-y-auto">
                  {lines.length === 0 ? (
                    <div className="text-slate-400">—</div>
                  ) : (
                    lines.map((l, idx) => (
                      <div key={idx} className={levelClass(l.level)}>
                        {l.message}
                      </div>
                    ))
                  )}
                  <div ref={tailRef} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

